import { NgTemplateOutlet } from '@angular/common';
import {
  afterRenderEffect,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  HostListener,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { Router } from '@angular/router';
import {
  MESSENGER_AUTH,
  MESSENGER_CONFIG,
  MessengerComposerComponent,
  MessengerFacade,
  MESSAGE_CONTENT_UNAVAILABLE,
  normalizeMessengerAttachment,
  parseGifMessageBody,
  type Message,
} from '@crm/messenger';
import {
  ButtonComponent,
  DragScrollDirective,
  IconComponent,
  SegmentedComponent,
  SheetComponent,
  StickToBottom,
  type SegmentedOption,
} from '@crm/ui';
import { CommandCenterState } from '../../pages/dashboard/command-center.state';
import { findLeadById } from '../../pages/leads/lead-data';
import { TELEPHONY_API_PORT } from '../data-access/telephony-api';
import { ContactActions, type ContactGif } from './contact-actions.service';
import { buildThreadEntries } from './thread-grouping';

const GIF_LIBRARY: readonly ContactGif[] = [
  { id: 'thumbs-up', label: 'Thumbs up', emoji: '👍' },
  { id: 'celebrate', label: 'Celebrate', emoji: '🎉' },
  { id: 'thank-you', label: 'Thank you', emoji: '🙏' },
  { id: 'new-home', label: 'New home', emoji: '🏡' },
  { id: 'savings', label: 'Savings', emoji: '💰' },
  { id: 'call-me', label: 'Call me', emoji: '📞' },
  { id: 'on-it', label: 'On it', emoji: '🚀' },
  { id: 'smile', label: 'Smile', emoji: '😊' },
  { id: 'high-five', label: 'High five', emoji: '🙌' },
] as const;

const LIST_PAGE_SIZE = 25;
const THREAD_PAGE_SIZE = 30;
const SCROLL_THRESHOLD_PX = 160;
const SEARCH_DEBOUNCE_MS = 300;
const RESIZE_SETTLE_MS = 320;

const QUICK_REPLIES = ['Calling you now', 'What’s a good time?', 'Send my scheduling link'] as const;

/**
 * Call-confirmation alert and Text sheet, mounted once in the app shell and
 * driven by {@link ContactActions}. Confirming a call opens the Live Call page.
 */
@Component({
  selector: 'contact-overlays',
  standalone: true,
  imports: [
    ButtonComponent,
    DragScrollDirective,
    IconComponent,
    NgTemplateOutlet,
    SegmentedComponent,
    SheetComponent,
    MessengerComposerComponent,
  ],
  templateUrl: './contact-overlays.html',
  styleUrl: './contact-overlays.css',
})
export class ContactOverlaysComponent {
  protected readonly t = {
    gifLibrary: 'GIF library',
    searchGifs: 'Search GIFs',
    noGifs: 'No GIFs found',
    gifLabel: 'GIF',
    send: "Send",
    textMessage: "Text message",
    textMessage2: "Text Message",
    unread: "Unread",
    conversations: "Conversations",
    call: "Call",
    cancel: "Cancel",
    callAlertDetail: "call-alert-detail",
    callAlertTitle: "call-alert-title",
    listUnavailable: 'Conversations could not be loaded.',
    threadUnavailable: 'This conversation is unavailable.',
    select: 'Select a conversation',
    dropTitle: 'Drop to Attach',
    dropHint: 'Release to add files to your message',
    empty: 'No conversations yet.',
    noResults: 'No results.',
    retry: 'Retry',
    delete: 'Delete',
    notDelivered: 'Not Delivered',
    contactCard: 'Contact card',
    search: 'Search',
    searchLabel: 'Search conversations',
    loading: 'Loading…',
    emailSubheading: 'Outlook (Microsoft Graph)',
    toLabel: 'To (recipients)',
    addRecipientPlaceholder: 'Add recipient email & press Enter…',
    addRecipientBtn: '+ Add',
    removeRecipientLabel: 'Remove',
    removeChar: '×',
    fromLabel: 'From',
    fromMailbox: 'Warren Dugan <warren.dugan@loandepot.com>',
    templateLabel: 'Template',
    noTemplateOption: 'No template selected',
    saveAsTemplateBtn: '+ Save as template',
    newTemplateNamePlaceholder: 'Template name (e.g. Rate lock follow-up)',
    saveTemplateConfirm: 'Save as new',
    overwriteTemplateConfirm: 'Overwrite current',
    saveTemplatePrompt: 'Save changes by overwriting the selected template or saving as a new template:',
    replaceTemplatePrompt: 'You already have text in your message. Replace it with the selected template?',
    replaceTemplateConfirm: 'Replace message',
    cancelTemplateBtn: 'Cancel',
    subjectLabel: 'Subject',
    subjectPlaceholder: 'Email subject',
    bodyLabel: 'Message',
    bodyPlaceholder: 'Write your email…',
    attachmentsLabel: 'Attachments',
    addAttachmentBtn: '+ Attach files',
    removeAttachmentLabel: 'Remove attachment',
    attachVCard: 'Attach my contact card (vCard)',
    openInOutlook: 'Open in Outlook',
    sendEmail: 'Send via Outlook',
    sendingEmail: 'Sending…',
    callSheetAriaLabel: 'Outbound call dialer',
    callSubheading: 'Outbound call',
    textSheetAriaLabel: 'Text messenger',
    emailSheetAriaLabel: 'Outlook email composer',
    panelLabel: 'Messenger workspace section',
    notesHeading: 'Notes',
    notePlaceholder: 'Enter a note…',
    addNoteBtn: 'Add note',
    appointmentHeading: 'Schedule Appointment',
    dateLabel: 'Date',
    timeLabel: 'Time',
    scheduleBtn: 'Schedule',
    schedulingBtn: 'Scheduling…',
    timelineHeading: 'Activity Timeline',
    noTimelineEntries: 'No activity recorded yet.',
    backToPanel: 'Back',
  } as const;

  readonly sheetPanelOptions: readonly SegmentedOption[] = [
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
    { value: 'notes', label: 'Notes' },
    { value: 'appointment', label: 'Appointment' },
    { value: 'timeline', label: 'Timeline' },
  ];

  readonly sheetOpen = computed(
    () => this.contacts.textOpen() || this.contacts.emailOpen(),
  );

  onSheetOpenChange(next: boolean): void {
    if (!next) {
      this.contacts.textOpen.set(false);
      this.contacts.emailOpen.set(false);
    }
  }

  selectSheetPanel(panel: 'sms' | 'email' | 'notes' | 'appointment' | 'timeline'): void {
    if (panel === 'email') {
      if (!this.contacts.emailOpen()) {
        this.contacts.email(this.contacts.activeId());
      }
      this.contacts.activePanel.set('email');
      return;
    }
    this.contacts.emailOpen.set(false);
    this.contacts.activePanel.set(panel);
    this.contacts.textOpen.set(true);
  }

  readonly overlayScheduledText = signal('');
  readonly overlayScheduleError = signal('');
  readonly overlayScheduling = signal(false);

  addOverlayNote(input: HTMLTextAreaElement): void {
    const text = input.value.trim();
    if (!text) return;
    this.contacts.addNote(this.contacts.activeId(), text);
    input.value = '';
  }

  async scheduleOverlayAppointment(
    dateInput: HTMLInputElement,
    timeInput: HTMLInputElement,
  ): Promise<void> {
    const dateVal = dateInput.value;
    const timeVal = timeInput.value;
    if (!dateVal || !timeVal || this.overlayScheduling()) return;
    this.overlayScheduling.set(true);
    this.overlayScheduleError.set('');
    try {
      const active = this.contacts.active();
      const response = await this.contacts.scheduleOutlookAppointment({
        leadIdOrName: active.id,
        startsAt: `${dateVal}T${timeVal}`,
        detail: `${active.loanPurpose ?? 'Mortgage'} consultation`,
      });
      if (response.status === 'booked' || response.status === 'noop') {
        this.overlayScheduledText.set(`${dateVal} at ${timeVal}`);
      } else {
        this.overlayScheduledText.set('');
        this.overlayScheduleError.set(
          response.errors[0]?.message ?? 'Could not schedule appointment.',
        );
      }
    } finally {
      this.overlayScheduling.set(false);
    }
  }

  protected readonly contacts = inject(ContactActions);
  protected readonly state = inject(CommandCenterState);
  private readonly telephonyApi = inject(TELEPHONY_API_PORT, { optional: true });
  protected readonly remote =
    inject(MESSENGER_CONFIG, { optional: true })?.mode === 'remote';
  protected readonly facade = this.remote ? inject(MessengerFacade) : null;
  private readonly threadWrap = viewChild<ElementRef<HTMLElement>>('threadWrap');
  private readonly callDialog = viewChild<ElementRef<HTMLElement>>('callDialog');
  private lastCallTrigger: HTMLElement | null = null;
  private wasCallOpen = false;
  private olderAnchor: { height: number; top: number } | null = null;
  private lastThreadKey = '';
  private lastThreadConversation = '';
  private follower: StickToBottom | null = null;
  private lastThreadWrap: HTMLElement | null = null;
  protected readonly expanded = signal(false);
  protected readonly fileDrag = signal(false);
  private readonly composer = viewChild(MessengerComposerComponent);
  protected readonly spokes = Array.from({ length: 12 }, (_, index) => index);
  protected readonly remoteView = signal<'list' | 'thread'>('list');
  protected readonly remoteHeading = computed(() => {
    if (this.contacts.emailOpen() || this.contacts.activePanel() === 'email') {
      return this.contacts.emailTarget().name;
    }
    if (this.contacts.activePanel() !== 'sms') {
      return this.contacts.active().name;
    }
    const conversation = this.facade?.thread()?.conversation;
    if (!this.expanded() && this.remoteView() === 'thread' && conversation) {
      return conversation.customerName;
    }
    return this.contacts.active().name || 'Messages';
  });
  protected readonly remoteSubheading = computed(() => {
    if (this.contacts.emailOpen() || this.contacts.activePanel() === 'email') {
      return this.t.emailSubheading;
    }
    if (this.contacts.activePanel() !== 'sms') {
      return this.contacts.active().detail;
    }
    const conversation = this.facade?.thread()?.conversation;
    if (!this.expanded() && this.remoteView() === 'thread' && conversation) {
      return conversation.customerPhone;
    }
    return this.contacts.active().detail;
  });
  private readonly auth = inject(MESSENGER_AUTH, { optional: true });
  protected readonly cardOwner = computed(() => {
    const account = this.auth?.account();
    const name = account?.name || account?.email || '';
    return {
      name: name || 'My contact card',
      initials: name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join('') || 'ME',
    };
  });
  protected readonly threadEntries = computed(() => {
    const merged = [...(this.facade?.thread()?.messages ?? [])];
    for (const pending of this.facade?.outgoingMessages() ?? []) {
      const index = merged.findIndex((item) => item.clientMessageId === pending.clientMessageId);
      if (index < 0) merged.push(pending);
      else merged[index] = pending;
    }
    return buildThreadEntries(merged);
  });
  protected readonly remotePins = computed(() => {
    const convs = this.facade?.conversations() ?? [];
    const seen = new Set<string>();
    return convs.filter((conv) => {
      const key =
        (conv.customerPhone ?? '').replace(/\D/g, '').slice(-10) ||
        conv.customerName.trim().toLowerCase() ||
        conv.id;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  });
  private searchTimer: ReturnType<typeof setTimeout> | undefined;
  private readonly router = inject(Router);
  protected readonly quickReplies = QUICK_REPLIES;
  protected readonly gifOpen = signal(false);
  protected readonly gifQuery = signal('');
  protected readonly gifs = computed(() => {
    const q = this.gifQuery().trim().toLowerCase();
    return GIF_LIBRARY.filter((gif) => !q || gif.label.toLowerCase().includes(q));
  });

  private findMatchingRemoteConversationId(): string | null {
    const facade = this.facade;
    if (!facade) return null;
    const person = this.contacts.active();
    const targetName = person.name.trim().toLowerCase();
    const targetDigits =
      (/\+?\d[\d\s().-]{7,}\d/.exec(person.detail)?.[0] ?? '')
        .replace(/\D/g, '')
        .slice(-10);
    for (const conv of facade.conversations()) {
      const convName = conv.customerName.trim().toLowerCase();
      const convDigits = (conv.customerPhone ?? '').replace(/\D/g, '').slice(-10);
      if (targetDigits && convDigits && targetDigits === convDigits) {
        return conv.id;
      }
      if (targetName && convName && (convName === targetName || convName.includes(targetName) || targetName.includes(convName))) {
        return conv.id;
      }
    }
    return null;
  }

  private pendingLeadAutoOpen = false;

  constructor() {
    effect(() => {
      if (!this.contacts.textOpen()) return;
      const activeId = this.contacts.activeId();
      const panel = this.contacts.activePanel();
      untracked(() => {
        void this.state.loadAvailability();
        if (!this.facade || panel !== 'sms') return;
        const person = this.contacts.personFor(activeId);
        const isSeedDefault = activeId === 'kevin-osei' || activeId === 'maria-delgado';
        this.pendingLeadAutoOpen = !isSeedDefault;
        const matchedId = this.findMatchingRemoteConversationId();
        if (matchedId && this.pendingLeadAutoOpen) {
          this.pendingLeadAutoOpen = false;
          this.openConversation(matchedId);
          return;
        }
        const searchTerm = !isSeedDefault && person.name ? person.name.trim() : '';
        this.remoteView.set('list');
        this.facade.setSearch(searchTerm);
        void this.facade.loadConversations({
          ...(searchTerm ? { search: searchTerm } : {}),
          pageSize: LIST_PAGE_SIZE,
        });
      });
    });
    effect(() => {
      const facade = this.facade;
      if (!facade || !this.contacts.textOpen() || this.contacts.activePanel() !== 'sms') return;
      if (facade.listStatus() !== 'ready') return;
      if (this.pendingLeadAutoOpen && this.remoteView() === 'list') {
        this.pendingLeadAutoOpen = false;
        const matchedId = this.findMatchingRemoteConversationId();
        if (matchedId) {
          untracked(() => this.openConversation(matchedId));
          return;
        }
        untracked(() => this.remoteView.set('thread'));
        return;
      }
      if (!this.expanded()) return;
      const first = facade.conversations()[0];
      if (first && !facade.selectedId()) untracked(() => this.openConversation(first.id));
    });
    inject(DestroyRef).onDestroy(() => this.follower?.dispose());
    afterRenderEffect(() => {
      const dialog = this.callDialog()?.nativeElement;
      if (dialog && !this.wasCallOpen) {
        this.wasCallOpen = true;
        this.lastCallTrigger =
          typeof document !== 'undefined' && document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;
        (
          dialog.querySelector<HTMLElement>('button[data-primary]') ??
          dialog.querySelector<HTMLElement>('button')
        )?.focus();
      } else if (!dialog && this.wasCallOpen) {
        this.wasCallOpen = false;
        if (this.lastCallTrigger?.isConnected) {
          this.lastCallTrigger.focus();
        }
        this.lastCallTrigger = null;
      }
    });
    afterRenderEffect(() => {
      const wrap = this.threadWrap()?.nativeElement;
      const entries = this.threadEntries();
      if (!wrap || !entries.length) return;
      const recreated = wrap !== this.lastThreadWrap;
      if (recreated) {
        this.follower?.dispose();
        const list = wrap.querySelector('[data-thread]');
        this.follower = new StickToBottom(wrap, list ? [list] : []);
      }
      this.lastThreadWrap = wrap;
      const follower = this.follower as StickToBottom;
      if (this.olderAnchor) {
        follower.release();
        wrap.scrollTop = wrap.scrollHeight - this.olderAnchor.height + this.olderAnchor.top;
        this.olderAnchor = null;
        return;
      }
      const conversation = this.facade?.selectedId() ?? '';
      const last = entries[entries.length - 1];
      const failed = this.facade?.outbox().some((entry) => entry.state === 'failed') ?? false;
      const key = `${last.key}|${failed}`;
      if (key === this.lastThreadKey && conversation === this.lastThreadConversation && !recreated) {
        return;
      }
      const changedConversation = conversation !== this.lastThreadConversation;
      const own =
        failed || (last.kind === 'message' && (last.message.pending || last.message.localOnly));
      this.lastThreadKey = key;
      this.lastThreadConversation = conversation;
      if (recreated || changedConversation || own) follower.pin();
      else if (follower.pinned) follower.scrollToBottom();
    });
  }

  onExpandedChange(expanded: boolean): void {
    this.expanded.set(expanded);
    if (!expanded && this.facade?.selectedId()) this.remoteView.set('thread');
    setTimeout(() => {
      const wrap = this.threadWrap()?.nativeElement;
      if (wrap) wrap.scrollTop = wrap.scrollHeight;
    }, RESIZE_SETTLE_MS);
  }

  private isFileDrag(event: DragEvent): boolean {
    return !!event.dataTransfer?.types.includes('Files');
  }

  private canDrop(): boolean {
    return !!this.facade?.selectedId() && (this.expanded() || this.remoteView() === 'thread');
  }

  onDragOver(event: DragEvent): void {
    if (!this.canDrop() || !this.isFileDrag(event)) return;
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    this.fileDrag.set(true);
  }

  onDragLeave(event: DragEvent): void {
    const related = event.relatedTarget;
    const host = event.currentTarget;
    if (related instanceof Node && host instanceof Node && host.contains(related)) return;
    this.fileDrag.set(false);
  }

  onDrop(event: DragEvent): void {
    this.fileDrag.set(false);
    if (event.defaultPrevented || !this.canDrop() || !this.isFileDrag(event)) return;
    event.preventDefault();
    void this.composer()?.addFiles(Array.from(event.dataTransfer?.files ?? []));
  }

  onSearch(event: Event): void {
    if (!this.facade) return;
    this.facade.setSearch((event.target as HTMLInputElement).value);
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(
      () => void this.facade?.loadConversations({ pageSize: LIST_PAGE_SIZE }),
      SEARCH_DEBOUNCE_MS,
    );
  }

  onPinsScroll(event: Event): void {
    const strip = event.target as HTMLElement;
    if (strip.scrollLeft + strip.clientWidth >= strip.scrollWidth - SCROLL_THRESHOLD_PX) {
      void this.facade?.loadMoreConversations();
    }
  }

  onInboxScroll(event: Event): void {
    const list = event.target as HTMLElement;
    if (list.scrollTop + list.clientHeight >= list.scrollHeight - SCROLL_THRESHOLD_PX) {
      void this.facade?.loadMoreConversations();
    }
  }

  onThreadScroll(event: Event): void {
    const wrap = event.target as HTMLElement;
    if (
      wrap.scrollTop > SCROLL_THRESHOLD_PX ||
      !this.facade?.threadHasMoreBefore() ||
      this.facade.threadLoadingOlder()
    ) {
      return;
    }
    this.olderAnchor = { height: wrap.scrollHeight, top: wrap.scrollTop };
    void this.facade.loadOlderMessages();
  }

  pinClick(id: string): void {
    if (this.facade) this.openConversation(id);
    else this.contacts.select(id);
  }

  openConversation(id: string): void {
    if (!this.facade) return;
    this.remoteView.set('thread');
    this.lastThreadKey = '';
    this.follower?.pin();
    void this.facade.selectConversation(id, { messageLimit: THREAD_PAGE_SIZE });
  }

  backToList(): void {
    this.pendingLeadAutoOpen = false;
    this.remoteView.set('list');
    if (this.facade) {
      this.facade.setSearch('');
      void this.facade.loadConversations({ pageSize: LIST_PAGE_SIZE });
    }
  }

  fromAgent(message: Message): boolean {
    return message.direction === 'outbound';
  }

  outboxErrorFor(id: string): string {
    return this.facade?.outbox().find((entry) => entry.key === id)?.error?.message ?? '';
  }

  textOf(message: Message): string {
    return message.text === MESSAGE_CONTENT_UNAVAILABLE && message.attachments.length > 0
      ? ''
      : message.text;
  }

  gifOf(message: Message) {
    return parseGifMessageBody(message.text);
  }

  attachmentsOf(message: Message) {
    return message.attachments.map(normalizeMessengerAttachment);
  }

  toggleGifs(): void {
    this.gifOpen.update((open) => !open);
    this.gifQuery.set('');
  }

  onGifQuery(event: Event): void {
    this.gifQuery.set((event.target as HTMLInputElement).value);
  }

  pickGif(gif: ContactGif): void {
    this.contacts.sendGif(gif);
    this.gifOpen.set(false);
  }

  onCallSheetOpenChange(open: boolean): void {
    if (!open) {
      this.contacts.cancelCall();
    }
  }

  @HostListener('document:keydown', ['$event'])
  onDocumentKeydown(event: KeyboardEvent): void {
    if (!this.contacts.callTarget() || event.defaultPrevented) return;
    if (event.key === 'Escape') {
      event.preventDefault();
      this.contacts.cancelCall();
    } else if (event.key === 'Tab') {
      this.trapCallDialogFocus(event);
    }
  }

  private trapCallDialogFocus(event: KeyboardEvent): void {
    const dialog = this.callDialog()?.nativeElement;
    if (!dialog) return;
    const focusable = Array.from(
      dialog.querySelectorAll<HTMLElement>('button:not([disabled])'),
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    const active =
      typeof document !== 'undefined' ? (document.activeElement as HTMLElement | null) : null;
    if (event.shiftKey) {
      if (!active || !dialog.contains(active) || active === first) {
        event.preventDefault();
        last?.focus();
      }
    } else if (!active || !dialog.contains(active) || active === last) {
      event.preventDefault();
      first?.focus();
    }
  }

  confirmCall(): void {
    const person = this.contacts.callTarget();
    this.contacts.cancelCall();
    if (person) {
      this.contacts.activeCallLeadId.set(person.id);
      this.contacts.activeCallConversationId.set(null);
      const remembered = findLeadById(person.id);
      const rawPhone =
        remembered?.mobilePhone ||
        remembered?.homePhone ||
        (/\+?\d[\d\s().-]{7,}\d/.exec(person.detail)?.[0] ?? '7145550142');
      const digits = rawPhone.replace(/\D/g, '').replace(/^1(\d{10})$/, '$1') || '7145550142';
      const dialNumber = digits.length === 10 ? `+1${digits}` : rawPhone.trim();
      const entryId = `tl-call-${Date.now()}`;
      this.contacts.recordTimelineEntry(person.id, {
        id: entryId,
        title: `Outbound call to ${person.name}`,
        detail: `Genesys Cloud Telephony · Dialing ${rawPhone}`,
        time: 'Just now',
        tone: 'green',
        kind: 'call',
      });
      void this.placeTelephonyCall(person.id, person.name, dialNumber, rawPhone);
      void this.router.navigate(['/calls/live'], { queryParams: { lead: person.id } });
    }
  }

  private ensureGenesysUserToken(): Promise<string | null> {
    if (typeof window === 'undefined') return Promise.resolve(null);
    try {
      const existing = window.localStorage.getItem('mello-genesys-token')?.trim();
      if (existing) return Promise.resolve(existing);
    } catch {
      // ignore storage errors
    }
    const isLocal = ['localhost', '127.0.0.1'].includes(window.location.hostname);
    const clientId = isLocal
      ? '57f62b94-abc1-4371-b414-54c520cd312a'
      : 'bb9790b2-dc57-46e4-8d6c-8717ef4eebf8';
    const redirectUri = isLocal
      ? 'http://127.0.0.1:8181'
      : `${window.location.origin}/assets/genesys-callback.html`;
    const authUrl = `https://login.usw2.pure.cloud/oauth/authorize?client_id=${encodeURIComponent(clientId)}&response_type=token&redirect_uri=${encodeURIComponent(redirectUri)}`;

    return new Promise<string | null>((resolve) => {
      let settled = false;
      const finish = (token: string | null) => {
        if (settled) return;
        settled = true;
        window.removeEventListener('message', onMessage);
        clearTimeout(timer);
        if (token) {
          try {
            window.localStorage.setItem('mello-genesys-token', token);
          } catch {
            // ignore
          }
        }
        resolve(token);
      };
      const onMessage = (event: MessageEvent) => {
        const data = event.data as { type?: string; accessToken?: string } | null;
        if (data?.type === 'genesys-oauth-token' && typeof data.accessToken === 'string' && data.accessToken.trim()) {
          finish(data.accessToken.trim());
        }
      };
      window.addEventListener('message', onMessage);
      const timer = setTimeout(() => finish(null), 15_000);
      const popup = window.open(authUrl, 'genesys-oauth', 'width=480,height=620');
      if (!popup) {
        finish(null);
      }
    });
  }

  private async placeTelephonyCall(
    leadId: string,
    personName: string,
    phoneNumber: string,
    displayPhone: string,
  ): Promise<void> {
    const api = this.telephonyApi;
    if (!api) return;
    let genesysToken = await this.ensureGenesysUserToken();
    let bffResult = await api.dial(
      {
        phoneNumber,
        externalContactPointId: `cp-${leadId}`,
      },
      genesysToken ? { genesysToken } : undefined,
    );
    if (bffResult.tag !== 'Ok' && genesysToken) {
      try {
        window.localStorage.removeItem('mello-genesys-token');
      } catch {
        // ignore
      }
      genesysToken = await this.ensureGenesysUserToken();
      if (genesysToken) {
        bffResult = await api.dial(
          {
            phoneNumber,
            externalContactPointId: `cp-${leadId}`,
          },
          { genesysToken },
        );
      }
    }
    if (bffResult.tag === 'Ok') {
      const convId = bffResult.value.conversationId;
      if (convId) {
        this.contacts.activeCallConversationId.set(convId);
        this.contacts.recordTimelineEntry(leadId, {
          id: `tl-conv-${Date.now()}`,
          title: `Genesys Call Connected (${personName})`,
          detail: `Dialed ${displayPhone} · Conversation ${convId}`,
          time: 'Just now',
          tone: 'green',
          kind: 'call',
        });
      }
      return;
    }
    const senderEmail = this.contacts.senderVCard().email || 'warren.dugan@loandepot.com';
    const userRes = await api.findUserByEmail(senderEmail);
    const agentUserId =
      userRes.tag === 'Ok' && userRes.value.id
        ? userRes.value.id
        : 'eab4f767-3b61-4b61-87d3-bedc9896b33c';
    const coreRes = await api.coreDial({
      agentUserId,
      phoneNumber,
      externalContactPointId: `cp-${leadId}`,
    });
    if (coreRes.tag === 'Ok' && coreRes.value.conversationId) {
      this.contacts.activeCallConversationId.set(coreRes.value.conversationId);
      this.contacts.recordTimelineEntry(leadId, {
        id: `tl-conv-${Date.now()}`,
        title: `Genesys Call Connected (${personName})`,
        detail: `Dialed ${displayPhone} · Conversation ${coreRes.value.conversationId}`,
        time: 'Just now',
        tone: 'green',
        kind: 'call',
      });
    }
  }

  send(input: HTMLInputElement): void {
    this.contacts.send(input.value);
    input.value = '';
  }

  quick(text: string): void {
    this.contacts.send(text);
  }

  onEmailRecipientInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = input.value;
    this.contacts.emailRecipientDraft.set(val);
    if (!val.trim()) {
      this.contacts.emailRecipientError.set('');
      return;
    }
    if (/[,\s;]$/.test(val)) {
      this.contacts.addEmailRecipient(val);
      input.value = this.contacts.emailRecipientDraft();
    } else if (this.contacts.emailRecipientError()) {
      this.contacts.emailRecipientError.set('');
    }
  }

  onEmailRecipientKeydown(event: KeyboardEvent): void {
    const isDelimiter =
      event.key === 'Enter' ||
      event.key === ' ' ||
      event.key === 'Spacebar' ||
      event.code === 'Space' ||
      event.key === ',' ||
      event.key === ';';
    if (isDelimiter) {
      if (this.contacts.emailRecipientDraft().trim()) {
        event.preventDefault();
        this.contacts.addEmailRecipient();
        const input = event.target as HTMLInputElement | null;
        if (input) {
          input.value = this.contacts.emailRecipientDraft();
        }
      }
    } else if (
      event.key === 'Backspace' &&
      !this.contacts.emailRecipientDraft() &&
      this.contacts.emailRecipients().length > 0
    ) {
      const current = this.contacts.emailRecipients();
      const last = current[current.length - 1];
      if (last) this.contacts.removeEmailRecipient(last);
    }
  }

  onEmailTemplateChange(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const outcome = this.contacts.selectEmailTemplate(select.value);
    if (outcome === 'confirm-replace') {
      select.value = this.contacts.emailTemplateId();
    }
  }

  confirmEmailTemplateReplace(): void {
    this.contacts.confirmReplaceEmailTemplate();
  }

  cancelEmailTemplateReplace(): void {
    this.contacts.cancelReplaceEmailTemplate();
  }

  toggleNewTemplateForm(): void {
    if (!this.contacts.newTemplateOpen() && !this.contacts.canSaveEmailTemplate()) {
      return;
    }
    this.contacts.newTemplateOpen.update((v) => !v);
  }

  onNewTemplateLabelInput(event: Event): void {
    this.contacts.newTemplateLabel.set((event.target as HTMLInputElement).value);
  }

  overwriteSelectedTemplate(): void {
    this.contacts.overwriteEmailTemplate();
  }

  saveNewTemplate(): void {
    this.contacts.saveCustomEmailTemplate();
  }

  onEmailAttachmentFilesSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files ?? []);
    if (files.length > 0) {
      void this.contacts.addEmailAttachments(files);
      input.value = '';
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  onEmailSubjectInput(event: Event): void {
    this.contacts.emailSubject.set((event.target as HTMLInputElement).value);
  }

  onEmailBodyInput(event: Event): void {
    this.contacts.emailBody.set((event.target as HTMLTextAreaElement).value);
  }

  submitEmail(): void {
    void this.contacts.sendEmail();
  }
}
